import { useState, useRef, useEffect } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import { FiCheckSquare } from "react-icons/fi";
import { MdCheckBoxOutlineBlank } from "react-icons/md";

const MultiSelectDropdown = ({
  label,
  options,
  selectedValues,
  onChange,
  onClick,
  placeholder = "Select options",
  isMulti = true, 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localSelectedValues, setLocalSelectedValues] = useState(
    selectedValues ? [...selectedValues] : []
  );
  const [isAllSelected, setIsAllSelected] = useState(false);
  const modalRef = useRef(null);
  const inputRef = useRef(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsModalOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Sync localSelectedValues with selectedValues whenever selectedValues changes
  useEffect(() => {
    setLocalSelectedValues(selectedValues ? [...selectedValues] : []);
  }, [selectedValues]);

  // Update "Select All" checkbox status
  useEffect(() => {
    setIsAllSelected(options.length > 0 && localSelectedValues.length === options.length);
  }, [localSelectedValues, options]);

  const handleOptionToggle = (option) => {
    if (!isMulti) {
     
      setLocalSelectedValues([option]);
    } else {

      const exists = localSelectedValues.some((item) => item.value  === option.value);
      if (exists) {
        setLocalSelectedValues(
          localSelectedValues.filter((item) => item.value !== option.value)
        );
      } else {
        setLocalSelectedValues([...localSelectedValues, option]);
      }
    }
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setLocalSelectedValues([]);
    } else {
      setLocalSelectedValues([...options]);
    }
  };

  const handleRemove = (option, e) => {
    e.stopPropagation();
    const updatedValues = localSelectedValues.filter(
      (item) => item.value !== option.value
    );
    setLocalSelectedValues(updatedValues);
    onChange(isMulti ? updatedValues : updatedValues[0] || null);
  };

  const handleDone = () => {
    onChange(isMulti ? localSelectedValues : localSelectedValues[0] || null);
    setIsModalOpen(false);
  };

  return (
    <div className="flex items-center">
      <label className="w-[30%] block text-sm font-medium text-gray-700">{label}</label>
      <div className="w-[70%] relative">
        <div
          ref={inputRef}
          className="flex flex-wrap gap-2 mb-2 p-2 border rounded-md cursor-pointer"
          onClick={() => {
            setIsModalOpen(true);
            if (onClick) {
              onClick();
            }
          }}
        >
          {localSelectedValues.length > 0 ? (
            <>
              {localSelectedValues.slice(0, 1).map((value) => (
                <div
                  key={value.value}
                  className="flex items-center bg-emerald-50 text-emerald-700 rounded-full px-3 py-1"
                >
                  <span className="text-sm">{value.label}</span>
                  <button
                    type="button"
                    className="ml-2 text-emerald-500 hover:text-emerald-700"
                    onClick={(e) => handleRemove(value, e)}
                  >
                    <IoCloseCircleOutline className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {localSelectedValues.length > 1 && (
                <div className="flex items-center bg-gray-200 text-gray-700 rounded-full px-3 py-1">
                  <span className="text-sm">+{localSelectedValues.length - 1}</span>
                </div>
              )}
            </>
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
        </div>

        {isModalOpen && (
          <div
            ref={modalRef}
            className="fixed inset-0 z-50 bg-gray-800 bg-opacity-50 flex justify-center items-center"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="bg-white w-full max-w-md p-4 rounded-md shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex gap-2 mb-4 py-2 px-3 rounded-lg bg-slate-100">
                <div className="flex items-center justify-between w-full">
                  <div>{label}</div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-xl bg-white rounded-full w-7 h-7 text-red-500"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="max-h-60 overflow-auto">
                {isMulti && (
                  <>
                    <div
                      className="flex items-center space-x-2 ml-5 cursor-pointer mb-3"
                      onClick={handleSelectAll}
                    >
                      <input
                        type="checkbox"
                        className="h-3 w-3 text-emerald-600 border-gray-300 rounded"
                        checked={isAllSelected}
                        onChange={() => {}}
                      />
                      <span className="text-sm text-gray-700">Select All</span>
                    </div>
                    <hr className="mb-3" />
                  </>
                )}

                {options.map((option) => {
                  const isSelected = localSelectedValues.some(
                    (item) => item.value === option.value
                  );
                  return (
                    <div
                      key={option.value}
                      className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleOptionToggle(option)}
                    >
                      <span className="mr-3">
                        {isSelected ? (
                          <FiCheckSquare className="text-emerald-600" />
                        ) : (
                          <MdCheckBoxOutlineBlank className="text-gray-300" />
                        )}
                      </span>
                      <span className="text-sm text-gray-700">{option.label}</span>
                    </div>
                  );
                })}
              </div>

              <button
                className="mt-4 w-full bg-emerald-600 text-white py-2 rounded-md hover:bg-emerald-700"
                onClick={handleDone}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;